import {IconButton, Theme, alpha} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {MutationSaveUserProfileRuleArgs, UserProfileRuleMatchType} from "@/api";
import {FormattedMessage, defineMessage} from "react-intl";
import UpdateForm from "@/components/extension/profiles/UpdateForm";
import {ExtensionProfileRuleAction, FeatureRestriction} from "@/api";
import {DragHandleIcon} from "@/components/widgets";
import {DeleteIcon} from "@/components/icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {FormattedAlert, EnumField, TextField} from "@dvrt/graphql-widgets";
import FormattedButton from "@/components/FormattedButton";
import {useRestrictedModeValue} from "@/components/extension/profiles/tools";
import {SortableContext, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {DragEndEvent, DndContext, closestCorners} from "@dnd-kit/core";
import {useEffect, useState} from "react";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {moveItemsInArray} from "@/tools";
import ErrorHelper from "@/utils/ErrorHelper";
import {EXTENSION_PROFILE_RULE_ACTION_MESSAGES, USER_PROFILE_RULE_MATCH_TYPE_MESSAGES} from "@/utils/translations";

const useStyles = makeStyles()((theme: Theme) => ({
    header: {
        margin: "6px 0",
        "& > div": {
            fontWeight: "bold",
        },
    },
    row: {
        display: "flex",
        padding: 2,
        backgroundColor: alpha(theme.palette.background.default, 0.7),
        backdropFilter: "blur(5px)",
        "& > div": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingRight: 4,
        },
    },
    actionCell: {
        width: "5%",
    },
    dataCell: {
        width: "23%",
    },
    dragCell: {
        width: "3%",
    },
    noRulesContainer: {
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
        marginTop: 3,
    },
    disabled: {
        pointerEvents: "none",
        opacity: 0.4,
    },
}));

type RowProps = {
    enabled: boolean;
    form: UpdateForm;
    rule: MutationSaveUserProfileRuleArgs;
    idx: number;
    sortId: number;
};

const RuleRow = ({enabled, form, rule, idx, sortId}: RowProps) => {
    const errorHelper = new ErrorHelper(form.getRuleErrors(idx), 1);

    const {setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition} = useSortable({
        id: sortId,
    });

    const {classes} = useStyles();

    const draggableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div className={classes.row} ref={setNodeRef} style={draggableStyle}>
            <div className={classes.dragCell}>
                <DragHandleIcon
                    setActivatorNodeRef={setActivatorNodeRef}
                    attributes={attributes}
                    listeners={listeners}
                />
            </div>
            <div className={classes.dataCell}>
                <TextField
                    value={rule.data.sourceNumber}
                    onChange={(value) => form.updateRuleValues(idx, {sourceNumber: value})}
                    disabled={!enabled}
                    errors={errorHelper.fieldErrors("sourceNumber")}
                />
            </div>
            <div className={classes.dataCell}>
                <EnumField
                    disabled={!enabled}
                    value={rule.data.matchType}
                    enum_={{UserProfileRuleMatchType}}
                    onChange={(matchType) => form.updateRuleValues(idx, {matchType})}
                    translations={USER_PROFILE_RULE_MATCH_TYPE_MESSAGES}
                />
            </div>
            <div className={classes.dataCell}>
                <EnumField
                    disabled={!enabled}
                    value={rule.data.action}
                    enum_={{ExtensionProfileRuleAction}}
                    onChange={(val: string) =>
                        form.updateRuleValues(idx, {
                            action: val as ExtensionProfileRuleAction,
                        })
                    }
                    translations={EXTENSION_PROFILE_RULE_ACTION_MESSAGES}
                />
            </div>
            <div className={classes.dataCell}>
                {rule.data.action === ExtensionProfileRuleAction.Forward && (
                    <TextField
                        value={rule.data.targetNumber}
                        onChange={(value) => form.updateRuleValues(idx, {targetNumber: value})}
                        disabled={!enabled}
                        errors={errorHelper.fieldErrors("targetNumber")}
                    />
                )}
            </div>
            <div className={classes.actionCell}>
                <IconButton color="secondary" size="small" onClick={() => form.deleteRule(idx)}>
                    <DeleteIcon />
                </IconButton>
            </div>
        </div>
    );
};

const RuleList = ({form, enabled}: {enabled: boolean; form: UpdateForm}) => {
    const getIdArray = (length: number) => Array.from({length}, (_, i) => i + 1);

    const [sortIds, setSortIds] = useState<number[]>(() => getIdArray(form.values.rules.length));

    useEffect(() => {
        setSortIds(getIdArray(form.values.rules.length));
    }, [form.values.rules.length]);

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        if (active.id !== over.id) {
            const oldIndex = sortIds.indexOf(active.id as number);
            const newIndex = sortIds.indexOf(over.id as number);

            form.reorderRules(oldIndex, newIndex);
            setSortIds((ids) => moveItemsInArray(ids, oldIndex, newIndex));
        }
    };

    return (
        <DndContext
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            collisionDetection={closestCorners}
        >
            <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                <div>
                    {form.values.rules.map((rule, idx) => (
                        <RuleRow
                            form={form}
                            enabled={enabled}
                            rule={rule}
                            key={sortIds[idx]}
                            idx={idx}
                            sortId={sortIds[idx]}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

const NoRulesWidget = ({enabled, form}: {enabled: boolean; form: UpdateForm}) => {
    const {classes} = useStyles();
    return (
        <div className={classes.noRulesContainer}>
            <FormattedAlert
                message={defineMessage({
                    id: "extensionProfileRule.noRuleMsg",
                    defaultMessage: 'There are no rules! Use "Add new rule".',
                })}
                severity="info"
                fullWidth
            />
            <FormattedButton
                disabled={!enabled}
                message={defineMessage({id: "extensionProfileRule.addNewRule", defaultMessage: "Add new rule"})}
                onClick={() => form.addNewRuleRow()}
            />
        </div>
    );
};

export const RuleWidget = ({enabled, form}: {enabled: boolean; form: UpdateForm}) => {
    const {classes, cx} = useStyles();

    const isRestrictedMode = useRestrictedModeValue();
    const restrictions = form.dataSource.data.current.extension.featuresRestrictions;

    const disabled = !enabled || (isRestrictedMode && restrictions.includes(FeatureRestriction.CallRoutings));

    if (!form.values.rules.length) return <NoRulesWidget form={form} enabled={!disabled} />;

    return (
        <div className={cx({[classes.disabled]: disabled})}>
            <div className={cx(classes.row, classes.header)}>
                <div className={classes.dragCell} />
                <div className={classes.dataCell}>
                    <FormattedMessage id="extensionProfileRule.sourceNumber" defaultMessage="Caller number" />
                </div>
                <div className={classes.dataCell}>
                    <FormattedMessage id="extensionProfileRule.matchType" defaultMessage="Matching rule" />
                </div>
                <div className={classes.dataCell}>
                    <FormattedMessage id="extensionProfileRule.action" defaultMessage="Action" />
                </div>
                <div className={classes.dataCell}>
                    <FormattedMessage id="extensionProfileRule.targetNumber" defaultMessage="Forward number" />
                </div>
                <div className={classes.actionCell}>
                    <IconButton color="primary" size="small" onClick={() => form.addNewRuleRow()}>
                        <FontAwesomeIcon icon="plus-square" />
                    </IconButton>
                </div>
            </div>
            <RuleList form={form} enabled={!disabled} />
        </div>
    );
};
